/**
 * 财务中心 - 子账号管理页
 *
 * 面向财务角色管理招商银行记账子单元，页面结构：
 * 1. 子账号列表：查询条件=服务商下拉（必选，进页自动选中第一项并查询，选中即查、
 *    清空不查询列表保留现状）+ 子单元下拉（选项为已加载的列表行，选中后前端本地过滤、
 *    清空恢复，全程不发请求，过滤作用范围=已加载数据）；查询按钮保留原位原逻辑；
 *    "新增子单元"按钮位于筛选行之下、列表之上独立一行靠左；
 *    列=服务商账号/子单元名称/子单元编号/生效日期/状态/是否可透支/支付失败退回方式/关闭条件；
 *    列表数据源为银行当前返回的子单元（已关闭的子单元银行不再返回，不出现在列表），
 *    银行查询失败时透出后端报错，不静默显示空列表；
 *    状态取银行实时返回（A=生效、W=银行处理中，无值显示空白）；
 *    三个银行配置列空值（银行未返回该行配置）显示空白；
 *    编号全 0 的默认子单元行修改/关闭按钮禁用；
 *    操作按钮使用系统统一的 plain 带边框样式；
 *    银行实时信息同步查询耗时较长，列表全程 loading，失败透出后端错误文案
 * 2. 滚动加载：银行单批返回一批子单元与续传游标，首屏一批，滚动到表格底部自动带游标追加下一批直到取完；
 *    查询条件变化（点查询、切服务商）后缓冲区与游标全量重置从首屏重拉；
 *    新增/修改/关闭成功后刷新列表=重置回首屏重拉；取完后提示"已全部加载"；
 *    银行续传中断时提示结果可能不完整
 * 3. 新增弹窗：标题旁问号悬浮展示银行"新增记账子单元"接口注意事项（11 条）；
 *    选服务商（带出账号/账户名）→ 搜索选中商户（下拉按服务商签约关系筛选可开户商户，
 *    未选服务商时禁用；选项展示商户名称与登录名；选中后自动带入子单元名称可改）→
 *    编号（前端预校验纯数字且恰好 10 位、不为全 0，重复校验以后端为准）+ 三个默认下拉
 *    （是否可透支默认可透支/支付失败退回方式默认退款至结算账户/关闭条件默认不限制）；
 *    必填校验不通过拦截不发请求
 * 4. 修改弹窗：上半段灰显只读回显服务商账号/子单元编号/绑定商户（系统商户名称）；
 *    下半段名称与三个下拉可改并预填现值，银行未返回该行配置时不预填、
 *    提示按网银现状选择，提交前强制显式选择，避免误覆盖
 * 5. 关闭：二次确认提示入账分类规则销毁与余额划回默认子单元，确认后调银行关闭
 * 6. 通用：银行调用耗时长，提交全程按钮 loading 防重复提交；银行"处理中"由后端统一文案提示；
 *    错误文案透出展示；操作成功后刷新列表
 *
 * 空值口径：状态与三个银行配置列空值显示空白（已无"--"占位列）
 */
<template>
  <div class="finance-subunit-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>财务中心</el-breadcrumb-item>
      <el-breadcrumb-item>子账号管理</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="search-section">
      <el-form :inline="true" :model="queryForm" size="small" class="search-form">
        <el-form-item label="服务商" required>
          <el-select
            v-model="queryForm.accountNumber"
            filterable
            clearable
            placeholder="请选择服务商"
            class="search-input"
            @change="handleProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
        </el-form-item>
        <!-- 子单元下拉为纯前端本地过滤：选项与列表严格同源（已加载数据），选中/清空均不发请求 -->
        <el-form-item label="子单元">
          <el-select
            v-model="queryForm.dmanbr"
            filterable
            clearable
            placeholder="请选择子单元"
            class="search-input"
          >
            <el-option
              v-for="row in rows"
              :key="row.dmanbr"
              :label="subunitLabel(row)"
              :value="row.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="toolbar">
      <el-button size="small" type="primary" icon="el-icon-plus" @click="openAddDialog">新增子单元</el-button>
    </div>

    <el-card shadow="hover" class="table-card">
      <div v-if="queried" class="load-info">
        <span>已加载 {{ filteredList.length }} 条<template v-if="rows.length && !hasMore">，已全部加载</template></span>
        <span v-if="scrollLoading" class="scroll-loading-text">加载中...</span>
        <el-alert
          v-if="truncated"
          type="warning"
          :closable="false"
          show-icon
          class="load-alert"
          title="银行续传中断，已加载结果可能不完整，可调整条件重新查询"
        ></el-alert>
      </div>

      <el-table
        ref="listTable"
        :data="filteredList"
        v-loading="listLoading"
        border
        size="small"
        height="520"
        empty-text="暂无数据"
      >
        <el-table-column prop="accountNumber" label="服务商账号" min-width="170"></el-table-column>
        <el-table-column prop="dmanam" label="子单元名称" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="dmanbr" label="子单元编号" min-width="150"></el-table-column>
        <el-table-column label="生效日期" width="110">
          <template slot-scope="{ row }">{{ eftdatText(row.eftdat) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">{{ stscodText(row.stscod) }}</template>
        </el-table-column>
        <el-table-column label="是否可透支" width="110">
          <template slot-scope="{ row }">{{ ovrctlText(row.ovrctl) }}</template>
        </el-table-column>
        <el-table-column label="支付失败退回方式" width="170">
          <template slot-scope="{ row }">{{ bcktypText(row.bcktyp) }}</template>
        </el-table-column>
        <el-table-column label="关闭条件" width="100">
          <template slot-scope="{ row }">{{ clstypText(row.clstyp) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="warning"
              size="mini"
              plain
              :disabled="row.defaultSubunit"
              @click="openEditDialog(row)"
            >修改</el-button>
            <el-button
              type="danger"
              size="mini"
              plain
              :disabled="row.defaultSubunit"
              :loading="closeSubmittingId === row.dmanbr"
              @click="handleClose(row)"
            >关闭</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- ==================== 新增弹窗 ==================== -->
    <el-dialog
      :visible.sync="addDialogVisible"
      width="620px"
      class="edit-form-dialog"
      :close-on-click-modal="false"
    >
      <template slot="title">
        <span>新增子单元</span>
        <!-- 银行新增记账子单元接口注意事项悬浮提示，避免财务误操作 -->
        <el-tooltip placement="right" effect="light" popper-class="subunit-tip-popper">
          <div slot="content">
            <div v-for="(tip, index) in subunitTips" :key="index" class="subunit-tip-line">{{ index + 1 }}. {{ tip }}</div>
          </div>
          <i class="el-icon-question subunit-tip-icon"></i>
        </el-tooltip>
      </template>
      <el-form :model="addForm" label-width="150px" size="small">
        <el-form-item label="服务商" required>
          <el-select
            v-model="addForm.accountNumber"
            filterable
            placeholder="请选择服务商"
            style="width: 100%"
            @change="handleAddProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
          <div v-if="selectedAddProvider" class="form-tip">
            账号：{{ selectedAddProvider.accountNumber }}　账户名：{{ selectedAddProvider.accountName }}
          </div>
        </el-form-item>
        <el-form-item label="商户" required>
          <el-select
            v-model="addForm.sysUserId"
            filterable
            remote
            clearable
            :disabled="!addForm.accountNumber"
            :remote-method="searchMerchants"
            :loading="merchantSearching"
            :placeholder="addForm.accountNumber ? '输入商户名称搜索' : '请先选择服务商'"
            style="width: 100%"
            @change="handleMerchantChange"
          >
            <el-option
              v-for="m in merchantOptions"
              :key="m.userId"
              :label="merchantLabel(m)"
              :value="m.userId"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="子单元名称" required>
          <el-input v-model="addForm.dmanam" placeholder="选中商户后自动带入，可修改" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="编号" required>
          <el-input v-model="addForm.dmanbr" placeholder="10 位纯数字" maxlength="10"></el-input>
        </el-form-item>
        <el-form-item label="是否可透支">
          <el-select v-model="addForm.ovrctl" style="width: 100%">
            <el-option label="可透支" value="Y"></el-option>
            <el-option label="不可透支" value="N"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="支付失败退回方式">
          <el-select v-model="addForm.bcktyp" style="width: 100%">
            <el-option label="退款至结算账户" value="N"></el-option>
            <el-option label="退款至该记账子单元" value="Y"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="关闭条件">
          <el-select v-model="addForm.clstyp" style="width: 100%">
            <el-option label="不限制" value="Y"></el-option>
            <el-option label="余额为零" value="N"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="handleAddSubmit">提交</el-button>
      </div>
    </el-dialog>

    <!-- ==================== 修改弹窗 ==================== -->
    <el-dialog
      title="修改子单元"
      :visible.sync="editDialogVisible"
      width="620px"
      class="edit-form-dialog"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" label-width="150px" size="small">
        <el-form-item label="服务商账号">
          <el-input :value="editForm.accountNumber" disabled></el-input>
        </el-form-item>
        <el-form-item label="子单元编号">
          <el-input :value="editForm.dmanbr" disabled></el-input>
        </el-form-item>
        <el-form-item label="绑定商户">
          <el-input :value="editRow.merchantName" disabled></el-input>
        </el-form-item>
        <el-form-item label="子单元名称" required>
          <el-input v-model="editForm.dmanam" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="是否可透支" required>
          <el-select v-model="editForm.ovrctl" placeholder="请选择" style="width: 100%">
            <el-option label="可透支" value="Y"></el-option>
            <el-option label="不可透支" value="N"></el-option>
          </el-select>
          <div v-if="!editForm.ovrctl" class="form-tip">银行未返回该行配置，请按网银现状选择</div>
        </el-form-item>
        <el-form-item label="支付失败退回方式" required>
          <el-select v-model="editForm.bcktyp" placeholder="请选择" style="width: 100%">
            <el-option label="退款至结算账户" value="N"></el-option>
            <el-option label="退款至该记账子单元" value="Y"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="关闭条件" required>
          <el-select v-model="editForm.clstyp" placeholder="请选择" style="width: 100%">
            <el-option label="不限制" value="Y"></el-option>
            <el-option label="余额为零" value="N"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="handleEditSubmit">提交</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getFinanceProviderList,
  getFinanceSubunitList,
  addFinanceSubunit,
  updateFinanceSubunit,
  closeFinanceSubunit,
  getFinanceSubunitMerchantList
} from '@/api/userMG';
import financeScrollList from './financeScrollList';

export default {
  name: 'FinanceSubunitManage',
  mixins: [financeScrollList],
  data() {
    return {
      // 银行"新增记账子单元"接口注意事项（新增弹窗问号悬浮提示内容）
      subunitTips: [
        '该接口用于新增记账子单元。',
        '记账子单元编号必须为数字，初始余额为0。',
        '客户开通灵账后，系统会自动为适用账户生成默认记账子单元（全0），并且以生效时结算户余额作为默认记账子单元的余额，在生效前不可使用。',
        '默认记账子单元不可透支。默认子单元不可新增。',
        '记账子单元允许透支时，对外付款的额度可以大于记账子单元的余额，但是不超过活期结算户的余额。请在上一笔交易对应的记账子单元余额明确更新后再发起下一笔交易。',
        '记账子单元支付失败退回方式：为"退款至该记账子单元"时，记账子单元代发出现退票等情况时，该退票交易会记入原支付记账子单元中。若原支付记账子单元在退票前关闭，则会退回到默认记账子单元中；若在退票前交易管家关闭，则自动退回到适用账号。（目前仅支持灵动代发失败退回场景）',
        '记账子单元关闭条件：不限制，则任何余额均可以关闭记账子单元；余额为零，检查余额为0才允许关闭记账子单元。',
        '凡对接"新增记账子单元-NTDMAADD"接口，须同步对接"查询记账子单元信息-NTDMALST"、"关闭记账子单元-NTDMADLT"。',
        '记账子单元配置实时生效。',
        '在初始化账户时，请尽量选择没有业务发生时。若出现余额不准确的情况，请使用内部调账接口自行调整。',
        '若该记账子单元关闭后再度开启，保留关闭时的余额和交易信息。'
      ],
      // 服务商下拉选项
      providerOptions: [],
      // 列表查询条件
      queryForm: {
        accountNumber: '',
        dmanbr: ''
      },
      listLoading: false,
      // 当前列表实际对应的服务商账号，修改/关闭操作以它为准，避免改选服务商未查询时误操作
      listAccountNumber: '',
      // 新增弹窗
      addDialogVisible: false,
      addForm: {
        accountNumber: '',
        sysUserId: '',
        dmanam: '',
        dmanbr: '',
        ovrctl: 'Y',
        bcktyp: 'N',
        clstyp: 'Y'
      },
      // 商户搜索下拉
      merchantOptions: [],
      merchantSearching: false,
      // 修改弹窗
      editDialogVisible: false,
      // 修改弹窗对应的列表行，只读段直接取行值，保证与列表展示同源
      editRow: {},
      editForm: {
        accountNumber: '',
        dmanbr: '',
        dmanam: '',
        ovrctl: '',
        bcktyp: '',
        clstyp: ''
      },
      submitting: false,
      // 正在提交关闭操作的行编号（行级 loading）
      closeSubmittingId: ''
    };
  },
  computed: {
    // 新增弹窗当前选中的服务商对象（用于带出账号/账户名）
    selectedAddProvider() {
      return this.providerOptions.find(p => p.accountNumber === this.addForm.accountNumber) || null;
    },
    // 子单元本地过滤后的列表（未选子单元时展示全部；过滤纯前端完成不发请求，作用范围=已加载数据）
    filteredList() {
      if (!this.queryForm.dmanbr) {
        return this.rows;
      }
      return this.rows.filter(item => item.dmanbr === this.queryForm.dmanbr);
    }
  },
  created() {
    this.loadProviders();
  },
  mounted() {
    this.bindTableScroll('listTable');
  },
  methods: {
    // 服务商下拉展示文案：账户名（账号）
    providerLabel(p) {
      return (p.accountName || '') + '（' + (p.accountNumber || '') + '）';
    },
    // 子单元下拉展示文案：名称（编号），默认子单元名称取不到时兜底展示
    subunitLabel(row) {
      const name = row.dmanam || (row.defaultSubunit ? '默认子单元' : '');
      return name + '（' + row.dmanbr + '）';
    },
    // 加载服务商下拉（招商银行记账子单元服务商账户）
    async loadProviders() {
      try {
        const res = await getFinanceProviderList();
        if (res && res.code === 0) {
          this.providerOptions = res.data || [];
          // 进页默认选中第一服务商并自动查询列表；下拉为空或用户已手动选择时不干预
          if (this.providerOptions.length > 0 && !this.queryForm.accountNumber) {
            this.queryForm.accountNumber = this.providerOptions[0].accountNumber;
            this.loadFirstBatch();
          }
        } else {
          this.$message.error((res && res.msg) || '服务商列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      }
    },
    // 状态列文案：银行实时返回，A=生效、W=银行处理中，无值显示空白
    stscodText(v) {
      if (v === 'A') return '生效';
      if (v === 'W') return '银行处理中';
      return '';
    },
    // 生效日期列文案：银行原值 yyyyMMdd 转 yyyy-MM-dd，空值显示空白
    eftdatText(v) {
      if (!v || v.length !== 8) return '';
      return v.slice(0, 4) + '-' + v.slice(4, 6) + '-' + v.slice(6, 8);
    },
    // 是否可透支配置列文案；空值（银行未返回该行配置）显示空白
    ovrctlText(v) {
      if (v === 'Y') return '可透支';
      if (v === 'N') return '不可透支';
      return '';
    },
    // 支付失败退回方式配置列文案；空值显示空白
    bcktypText(v) {
      if (v === 'Y') return '退款至该记账子单元';
      if (v === 'N') return '退款至结算账户';
      return '';
    },
    // 关闭条件配置列文案；空值显示空白
    clstypText(v) {
      if (v === 'Y') return '不限制';
      if (v === 'N') return '余额为零';
      return '';
    },
    // 编号前端预校验；返回空字符串表示通过，否则返回提示文案
    validateDmanbr(v) {
      if (!v) return '请输入编号';
      if (!/^\d+$/.test(v)) return '编号必须为 10 位纯数字';
      if (v.length !== 10) return '编号必须为 10 位纯数字';
      if (/^0+$/.test(v)) return '编号不能为全 0';
      return '';
    },
    // 服务商下拉选中即查：从首屏重拉列表；清空不查询、列表保留现状，
    // 此时点查询按钮走"请先选择服务商"拦截；切换服务商时同步清空子单元筛选，避免旧编号残留
    handleProviderChange(val) {
      if (!val) {
        return;
      }
      this.queryForm.dmanbr = '';
      this.loadFirstBatch();
    },
    handleSearch() {
      if (!this.queryForm.accountNumber) {
        this.$message.warning('请先选择服务商');
        return;
      }
      // 查询条件变化后从首屏重拉
      this.loadFirstBatch();
    },
    // 当前查询条件参数（不含游标；续传游标由滚动加载逻辑自动并入）
    buildQueryParams() {
      return { accountNumber: this.queryForm.accountNumber };
    },
    // 调列表接口拉一批（数据源为银行当前返回的子单元）；业务失败提示后返回 null，缓冲区保持不动
    async fetchBatch(params) {
      const res = await getFinanceSubunitList(params);
      if (res && res.code === 0) {
        return res.data || {};
      }
      this.$message.error((res && res.msg) || '列表加载失败');
      return null;
    },
    // 本批落库后固化服务商账号（追加与首屏同条件，仅首屏赋值即可）
    afterBatchLoaded(append) {
      if (!append) {
        this.listAccountNumber = this.queryForm.accountNumber;
      }
    },
    // 商户远程搜索（财务专用接口：按已选服务商的签约关系筛选可开户商户，按商户名称模糊匹配）
    async searchMerchants(query) {
      if (!query || !this.addForm.accountNumber) {
        this.merchantOptions = [];
        return;
      }
      this.merchantSearching = true;
      try {
        const res = await getFinanceSubunitMerchantList({
          accountNumber: this.addForm.accountNumber,
          merchantName: query
        });
        if (res && res.code === 0) {
          this.merchantOptions = (res.data || []).map(m => ({
            userId: String(m.userId),
            merchantName: m.merchantName,
            username: m.username
          }));
        } else {
          this.merchantOptions = [];
        }
      } catch (e) {
        this.merchantOptions = [];
      } finally {
        this.merchantSearching = false;
      }
    },
    // 商户下拉选项文案：商户名称（登录名），同名商户时用于区分
    merchantLabel(m) {
      if (m.username) {
        return m.merchantName + '（' + m.username + '）';
      }
      return m.merchantName;
    },
    // 选中商户后自动带入子单元名称（可修改）
    handleMerchantChange(sysUserId) {
      const m = this.merchantOptions.find(item => item.userId === sysUserId);
      if (m) {
        this.addForm.dmanam = m.merchantName;
      }
    },
    // 新增弹窗切换服务商时清空商户选择与带入的名称，避免跨服务商残留
    handleAddProviderChange() {
      this.addForm.sysUserId = '';
      this.addForm.dmanam = '';
      this.merchantOptions = [];
    },
    openAddDialog() {
      this.addForm = {
        accountNumber: this.queryForm.accountNumber || '',
        sysUserId: '',
        dmanam: '',
        dmanbr: '',
        ovrctl: 'Y',
        bcktyp: 'N',
        clstyp: 'Y'
      };
      this.merchantOptions = [];
      this.addDialogVisible = true;
    },
    // 新增提交：必填与编号预校验不通过拦截不发请求
    async handleAddSubmit() {
      if (!this.addForm.accountNumber) {
        this.$message.warning('请选择服务商');
        return;
      }
      if (!this.addForm.sysUserId) {
        this.$message.warning('请搜索并选择商户');
        return;
      }
      if (!this.addForm.dmanam || !this.addForm.dmanam.trim()) {
        this.$message.warning('请输入子单元名称');
        return;
      }
      const dmanbrError = this.validateDmanbr(this.addForm.dmanbr);
      if (dmanbrError) {
        this.$message.warning(dmanbrError);
        return;
      }
      this.submitting = true;
      try {
        const res = await addFinanceSubunit({
          sysUserId: this.addForm.sysUserId,
          accountNumber: this.addForm.accountNumber,
          dmanbr: this.addForm.dmanbr,
          dmanam: this.addForm.dmanam.trim(),
          ovrctl: this.addForm.ovrctl,
          bcktyp: this.addForm.bcktyp,
          clstyp: this.addForm.clstyp
        });
        if (res && res.code === 0) {
          this.$message.success('新增成功');
          this.addDialogVisible = false;
          this.loadFirstBatch();
        } else {
          this.$message.error((res && res.msg) || '新增失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg（含银行错误文案），此处仅兜底
      } finally {
        this.submitting = false;
      }
    },
    openEditDialog(row) {
      // 只读段直接取列表行值；三个下拉预填列表行的配置现值（银行返回），
      // 银行未返回该行配置时不预填，提交前强制显式选择
      this.editRow = row;
      this.editForm = {
        accountNumber: this.listAccountNumber,
        dmanbr: row.dmanbr,
        dmanam: row.dmanam || '',
        ovrctl: row.ovrctl || '',
        bcktyp: row.bcktyp || '',
        clstyp: row.clstyp || ''
      };
      this.editDialogVisible = true;
    },
    // 修改提交：名称与三个下拉四项显式全传，账号/编号为查询条件不可改
    async handleEditSubmit() {
      if (!this.editForm.dmanam || !this.editForm.dmanam.trim()) {
        this.$message.warning('请输入子单元名称');
        return;
      }
      if (!this.editForm.ovrctl) {
        this.$message.warning('请选择是否可透支');
        return;
      }
      if (!this.editForm.bcktyp) {
        this.$message.warning('请选择支付失败退回方式');
        return;
      }
      if (!this.editForm.clstyp) {
        this.$message.warning('请选择关闭条件');
        return;
      }
      this.submitting = true;
      try {
        const res = await updateFinanceSubunit({
          accountNumber: this.editForm.accountNumber,
          dmanbr: this.editForm.dmanbr,
          dmanam: this.editForm.dmanam.trim(),
          ovrctl: this.editForm.ovrctl,
          bcktyp: this.editForm.bcktyp,
          clstyp: this.editForm.clstyp
        });
        if (res && res.code === 0) {
          this.$message.success('修改成功');
          this.editDialogVisible = false;
          this.loadFirstBatch();
        } else {
          this.$message.error((res && res.msg) || '修改失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg（含银行错误文案），此处仅兜底
      } finally {
        this.submitting = false;
      }
    },
    // 关闭：二次确认后调银行关闭，成功刷新列表（重置回首屏重拉）
    handleClose(row) {
      this.$confirm('该子单元入账分类规则将被销毁、余额自动划回默认子单元，是否确认关闭？', '关闭确认', {
        confirmButtonText: '确认关闭',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          this.closeSubmittingId = row.dmanbr;
          try {
            const res = await closeFinanceSubunit({
              accountNumber: this.listAccountNumber,
              dmanbr: row.dmanbr
            });
            if (res && res.code === 0) {
              this.$message.success('关闭成功');
              this.loadFirstBatch();
            } else {
              this.$message.error((res && res.msg) || '关闭失败');
            }
          } catch (e) {
            // HTTP 400/500 由响应拦截器统一弹出后端 msg（含银行"处理中"文案），此处仅兜底
          } finally {
            this.closeSubmittingId = '';
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.finance-subunit-page {
  padding-bottom: 24px;
}
.page-breadcrumb {
  margin-bottom: 0;
}
.search-section {
  margin-top: 24px;
  margin-bottom: 16px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.search-form .el-form-item {
  margin-bottom: 10px;
  margin-right: 20px;
}
.search-form .search-input {
  width: 260px;
}
.toolbar {
  margin: 16px 0;
}
.table-card {
  border-radius: 8px;
  overflow: hidden;
}
.table-card >>> .el-card__body {
  padding: 20px;
}
.edit-form-dialog >>> .el-dialog {
  border-radius: 8px;
}
.edit-form-dialog >>> .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}
.edit-form-dialog >>> .el-dialog__title {
  font-size: 16px;
  color: #303133;
}
.edit-form-dialog >>> .el-dialog__body {
  padding: 16px 20px 10px;
}
.edit-form-dialog >>> .el-dialog__body .el-form-item {
  margin-bottom: 12px;
}
.edit-form-dialog >>> .el-dialog__footer {
  padding: 12px 20px 20px;
  text-align: right;
}
.load-info {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
}
.scroll-loading-text {
  margin-left: 12px;
  color: #909399;
}
.load-alert {
  margin-top: 6px;
  padding: 4px 12px;
}
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 20px;
  margin-top: 2px;
}
.subunit-tip-icon {
  margin-left: 6px;
  color: #909399;
  cursor: pointer;
}
</style>

<style>
/* 新增子单元弹窗注意事项浮层：tooltip 挂在 body 下，须用全局样式控制宽度与行距 */
.subunit-tip-popper {
  max-width: 420px;
  line-height: 20px;
  font-size: 12px;
}
.subunit-tip-popper .subunit-tip-line {
  margin-bottom: 4px;
}
.subunit-tip-popper .subunit-tip-line:last-child {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/subunitManage.vue